import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ForeignKeyValidator } from '../../../../core/_infrastructure/classes/foreignKeyValidator';
import { ExceptionManager } from '../../../../core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from '../../../../core/_infrastructure/classes/services/auth.service';
import { NotificationService } from '../../../../core/_infrastructure/classes/services/notification.service';
import { GlobalService } from '../../../../core/_infrastructure/classes/services/global.service';
import { Tc } from '../../../../core/_infrastructure/types/common';
import { RestriccionProductosExService } from '../restriccionproductoex.service';
import { RestriccionProductosExViewModel } from '../restriccionproductoex.types';
import { ComponentBase } from '../../../../core/_infrastructure/classes/componentBase';

@Component({
    selector: 'app-restriccionproductoex-wrapper-edit',
    templateUrl: './restriccionproductoex-wrapper-edit.component.html',
})
export class RestriccionProductosExWrapperEditComponent extends ComponentBase implements OnInit {
    visibilitySpinner = false;
    restriccion: RestriccionProductosExViewModel;
    formGroup: FormGroup;

    cloneMode = false;

    messageErrorAlreadyExist = 'Ya existe esta combinación de País y Especie';

    succesCreateMessage = 'Restricción creada correctamente';
    successEditMessage = 'Restricción modificada correctamente';

    listUrl = '/mantenimiento/especies/restriccionproductosex';

    activatedRouteData$ = this.activatedRoute.data.subscribe(data => {
        this.cloneMode = data['clone'] === true;
    });

    activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const idEspecie: any = params.get('idEspecie');
        const idPaisDestino: any = params.get('idPaisDestino');
        if (idEspecie && idPaisDestino) {
            this.getRestriccion(idEspecie, idPaisDestino);
        }
    });

    listPais: Tc[];

    listPais$ = this.globalService.getAllPaises().subscribe(params => {
        this.listPais = params;
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private restriccionProductosExService: RestriccionProductosExService,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        protected globalService: GlobalService,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            basic: this.fb.group({
                IdEspecie: [null, ForeignKeyValidator.cannotBeNullOrZero],
                IdPaisDestino: [null, Validators.required],
                FechaCreacion: null,
                FechaBaja: null,
                Activa: [true, Validators.required],
                IdGrupo: [null, Validators.required],
                IdGrupoDescripcion: '',
                IdForma: [null],
                IdFormaDescripcion: '',
                CodigoEppo: [''],
                NombreCientifico: [''],
                NombreComun: [''],
            }),
        });
    }

    private getRestriccion(idEspecie: number, idPaisDestino: number): void {
        this.restriccionProductosExService
            .getRestriccionProductosEx(idEspecie, idPaisDestino)
            .pipe(
                tap((rest: RestriccionProductosExViewModel) => {
                    this.restriccion = rest;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    this.formGroup.disable();
                    return of([]);
                }),
            )
            .subscribe();
    }

    onSave(): void {
        this.visibilitySpinner = true;
        const model = { ...this.formGroup.value.basic };

        model.FechaCreacion = this.restriccion?.FechaCreacion;
        model.FechaBaja = this.restriccion?.FechaBaja;

        if (this.cloneMode && model.Activa === true) {
            model.FechaBaja = null;
        }

        if (!this.restriccion || this.cloneMode) {
            this.restriccionProductosExService
                .create(model)
                .pipe(
                    tap(res => {
                        this.visibilitySpinner = false;
                        this.addSuccessMessage(this.succesCreateMessage);
                        this.onBack();
                    }),
                    catchError(error => {
                        this.catchServerError(error);
                        this.visibilitySpinner = false;
                        if (error?.message?.indexOf(this.messageErrorAlreadyExist) != -1) {
                            this.formGroup.disable();
                        }
                        return of([]);
                    }),
                )
                .subscribe();
        } else {
            this.restriccionProductosExService
                .edit(model)
                .pipe(
                    tap(res => {
                        this.visibilitySpinner = false;
                        this.addSuccessMessage(this.successEditMessage);
                        this.onBack();
                    }),
                    catchError(error => {
                        this.catchServerError(error);
                        this.visibilitySpinner = false;
                        if (error?.message?.indexOf(this.messageErrorAlreadyExist) != -1) {
                            this.formGroup.disable();
                        }
                        return of([]);
                    }),
                )
                .subscribe();
        }
    }

    get title(): string {
        if (!this.cloneMode) {
            return this.restriccion ? 'Editar' : 'Nueva';
        } else {
            return 'Clonar';
        }
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    get formInvalid(): boolean {
        return !this.formGroup.valid || !this.formGroup.dirty || this.formGroup.pristine;
    }
}
